import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useCartStore } from '../../store/cartStore';
import type { Product } from '../../types/product';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Header } from '../../components/layout/Header';

export default function HomePage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const addItem = useCartStore((state) => state.addItem);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(8);

      if (error) throw error;

      setProducts(data || []);
      
      const uniqueCategories = [...new Set(data?.map(p => p.category) || [])];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('상품 조회 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product: Product) => {
    addItem(product, 1);
    alert('장바구니에 추가되었습니다!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-white">
      {/* 네비게이션 */}
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1f6412c4eb6c98b604acd5facdb7eb50.jpg"
            alt="제주 귤밭"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-orange-900/40 via-orange-800/30 to-orange-900/50"></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-5xl px-4 w-full">
          <div className="mb-8 inline-block">
            <div className="w-20 h-20 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <i className="ri-plant-line text-5xl text-white"></i>
            </div>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 tracking-tight" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            정희네농원
          </h1>
          <p className="text-2xl md:text-3xl mb-4 font-light">청정 제주에서 온 달콤한 행복</p>
          <p className="text-lg md:text-xl mb-12 text-orange-100 max-w-2xl mx-auto">
            노지귤, 한라봉, 천혜향, 황금향까지<br />
            제주의 프리미엄 감귤을 신선하게 배송해드립니다
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/products" 
              className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-full text-lg font-bold transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line mr-2"></i>
              상품 둘러보기
            </Link>
            <a 
              href="#categories" 
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white px-10 py-4 rounded-full text-lg font-bold transition-all whitespace-nowrap"
            >
              카테고리 보기
            </a>
          </div>
        </div>

        {/* 스크롤 인디케이터 */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white/80"></i>
        </div>
      </section>

      {/* 특징 섹션 */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-yellow-50 hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-plant-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">제주 직송</h3>
              <p className="text-gray-600 leading-relaxed">청정 제주에서 수확 즉시 신선하게 배송되는 프리미엄 감귤을 만나보세요.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-yellow-50 hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">품질 보증</h3>
              <p className="text-gray-600 leading-relaxed">엄격한 품질 검사를 거친 최상품만을 선별하여 고객님께 전달합니다.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-yellow-50 hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-truck-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">빠른 배송</h3>
              <p className="text-gray-600 leading-relaxed">주문 후 1-2일 내 신선한 상태로 안전하게 배송해드립니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 카테고리 섹션 */}
      {categories.length > 0 && (
        <section id="categories" className="py-20 px-4 bg-gradient-to-br from-orange-50 to-yellow-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                프리미엄 감귤 라인업
              </h2>
              <p className="text-xl text-gray-600">제주의 자랑, 다양한 품종의 감귤을 만나보세요</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link
                  key={category}
                  to={`/products?category=${encodeURIComponent(category)}`}
                  className="group bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all hover:scale-105 cursor-pointer"
                >
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center group-hover:from-orange-200 group-hover:to-yellow-200 transition-all">
                    <i className="ri-leaf-line text-4xl text-orange-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 group-hover:text-orange-500 transition-colors">{category}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 인기 상품 */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              베스트 상품
            </h2>
            <p className="text-xl text-gray-600">고객님들이 가장 많이 찾는 인기 상품</p>
          </div>
          
          {products.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center">
                <i className="ri-shopping-bag-line text-5xl text-orange-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-3">등록된 상품이 없습니다</h3>
              <p className="text-gray-500 mb-8">곧 신선한 제주 감귤을 만나보실 수 있습니다</p>
              {user && (
                <Link
                  to="/admin/products"
                  className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-full text-base font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  상품 등록하기
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
                {products.map((product) => (
                  <div key={product.id} className="group bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-105">
                    <Link to={`/product/${product.id}`} className="block">
                      <div className="relative overflow-hidden aspect-square">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                          신선
                        </div>
                      </div>
                    </Link>
                    <div className="p-6">
                      <Link to={`/product/${product.id}`}>
                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold text-orange-500">
                          {product.price.toLocaleString()}원
                        </span>
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-shopping-cart-line mr-2"></i>
                        장바구니 담기
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-12">
                <Link
                  to="/products"
                  className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-4 rounded-full text-lg font-bold transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap"
                >
                  전체 상품 보기
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* 제주 스토리 */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                제주의 햇살과<br />바람을 담았습니다
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                청정 제주의 화산토에서 자란 감귤은 다릅니다. 풍부한 미네랄과 일조량, 그리고 농부의 정성이 만들어낸 최고의 맛을 경험해보세요.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                노지귤의 새콤달콤함부터 한라봉의 진한 향, 천혜향의 달콤함, 황금향의 고급스러운 맛까지. 제주 감귤의 모든 것을 만나보실 수 있습니다.
              </p>
              <Link
                to="/products"
                className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-10 py-4 rounded-full text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                지금 주문하기
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=beautiful%20jeju%20island%20landscape%20with%20tangerine%20orange%20orchard%20under%20blue%20sky%20with%20mount%20hallasan%20in%20background%20artistic%20illustration%20style%20showing%20lush%20green%20trees%20with%20bright%20orange%20fruits%20peaceful%20natural%20scenery&width=600&height=600&seq=story-img&orientation=squarish"
                  alt="제주 감귤밭"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-2xl">
                <div className="text-center text-white">
                  <div className="text-3xl font-bold">100%</div>
                  <div className="text-sm">제주산</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-orange-600 to-orange-700 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>정희네농원</h3>
              <p className="text-orange-100 leading-relaxed">
                청정 제주에서 재배한 프리미엄 감귤을 신선하게 배송해드립니다.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">고객센터</h4>
              <ul className="space-y-2 text-orange-100">
                <li><i className="ri-phone-line mr-2"></i>1588-0000</li>
                <li><i className="ri-mail-line mr-2"></i>help@jeonghee-farm.com</li>
                <li><i className="ri-time-line mr-2"></i>평일 09:00 - 18:00</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">바로가기</h4>
              <ul className="space-y-2 text-orange-100">
                <li><Link to="/products" className="hover:text-white transition-colors">전체 상품</Link></li>
                <li><Link to="/cart" className="hover:text-white transition-colors">장바구니</Link></li>
                <li><Link to="/mypage" className="hover:text-white transition-colors">마이페이지</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-orange-500 pt-8 text-center text-orange-100">
            <p className="mb-2">© 2024 정희네농원. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}